



























































































































import 'leaflet/dist/leaflet.css';
import Vue, { CreateElement, VNode } from 'vue';
import { Component, Prop, Watch, Provide } from 'vue-property-decorator';
import _debounce from 'lodash/debounce';
import _differenceWith from 'lodash/differenceWith';
import L, { LatLngBounds } from 'leaflet';
require('leaflet-easyprint');
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import Color from 'color';
import { SwExtendedVue } from '../sw-extended-vue';
import { utilService } from '../util-service';
import { i18nService } from '../i18n-service';
import { domService } from '../dom-service';
import SwMapZoom from './controls/sw-map-zoom.vue';
import SwMapLayer from './controls/sw-map-layer.vue';
import {
  MapLocation, MarkerConfig, EasyPrint, MapLocationDescriptorMap,
  InternalLocationDescriptor, MapMarkerConfig, MapDetailViewConfig, DetailViewLayerDescriptor,
  DetailViewConfig, DetailViewInterestDescriptor, DetailViewInterest, DetailViewLayer, PopupConfig,
  MapLayerDescriptor, DetailViewLayerActionEventData, MapLocationRelocatedEventData,
  InterestRelocatedEventData,
  ExtendedMarkerClusterGroupOptions,
  ExtendedMarkerCluster,
  ExtendedMapBoundsOptions,
} from './types';
import { MapControlledLayer } from './controls/types';

const cssPrefix = 'sw-map';

/**
 * @event zoom-change `(value: number)`
 * @event enter-detail-view `(locationId: string)`
 * @event leave-detail-view `(locationId: string)`
 * @event viewing-detail-layer `(layerId: string)`
 * @event reset-map
 * @event layer-action `(DetailViewLayerActionEventData)`
 * @event location-relocated `(MapLocationRelocatedEventData)`
 * @event interest-relocated `(InterestRelocatedEventData)`
 */
@Component({
  components: {
    SwMapZoom,
    SwMapLayer,
  },
})
export default class SwMap extends SwExtendedVue {
  @Prop({ type: String, default: 'osm' })
  server: 'osm' | 'google';

  @Prop({ type: Array, default () { return [0, 0]; } })
  initCenter: [number, number];

  @Prop({ type: Number, default: 1 })
  initZoom: number;

  @Prop({ type: Array, default () { return []; } })
  locations: MapLocation[];

  @Prop({ type: Object, default() { return {}; } })
  markerConfig: MapMarkerConfig;

  @Prop({ type: Object, default() { return {}; } })
  detailViewConfig: MapDetailViewConfig;

  @Prop({ type: Boolean, default: false })
  enableExport: boolean;

  @Prop({ type: Boolean, default: true })
  enableInteraction: boolean;

  @Prop({ type: Boolean, default: true })
  enableControl: boolean;

  @Prop({ type: Boolean, default: true })
  enableLayerControl: boolean;

  @Prop({ type: [Boolean, Object], default: false })
  withMarkerCluster: boolean | ExtendedMarkerClusterGroupOptions;

  @Prop({ type: [Boolean, Number], default: false })
  minimumZoom: boolean | number;

  @Prop({ type: [Boolean , Object], default: false })
  mapBounds: ExtendedMapBoundsOptions | boolean;

  cssPrefix = cssPrefix;

  public mapObj: L.Map | null = null;
  private baseLayer: L.TileLayer | null = null; // manage the tiles
  private locationDescMap: MapLocationDescriptorMap = {}; // manage all location markers
  private markerClusterGroup: L.MarkerClusterGroup | null = null; // manage marker clusters if enabled

  private zoom: number = this.initZoom;
  private zoomChangeFromControl: boolean = false;

  private printer: EasyPrint | null = null;

  private isLayerControlVisible = false;
  private viewDetailLocationId: string | null = null;
  private viewDetailLayerId: string | null = null;

  get hasDetailView(): boolean {
    return !this.locations.every(l => (!l.detailView));
  }

  get mapZoomConfig() {
    const maxRequestZoom = 17;
    return {
      maxRequestZoom, // level higher than this would not trigger tile request.
      maxZoom: maxRequestZoom + (this.hasDetailView ? 3 : 0),
    };
  }

  get baseLayerSource() {
    return {
      /* tslint:disable-next-line:max-line-length */
      googleMap: 'https://www.google.com/maps/vt?pb=!1m5!1m4!1i{z}!2i{x}!3i{y}!4i256!2m3!1e0!2sm!3i342009817!3m9!2sen-US!3sCN!5e18!12m1!1e47!12m3!1e37!2m1!1ssmartmaps!4e0&token=32965',
      osmMap: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      offlineMap: 'http://127.0.0.1:8081/{z}/{x}/{y}.png',
    };
  }

  get serverOptions(): { sourceUrl: string; attribution: string; } {
    switch (this.server) {
      case 'google': return {
        sourceUrl: this.baseLayerSource.googleMap,
        attribution: '&copy; <a href="https://map.google.com/">Google</a>',
      };
      case 'osm':
      default: return {
        sourceUrl: this.baseLayerSource.osmMap,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      };
    }
  }

  get normalizedGlobalMarkerConfig(): MarkerConfig {
    return utilService.assignWithoutUndefined(
      { name: 'location', size: 22, color: utilService.colorConst.statusSuccess, rotate: 0, anchorOffset: [0, 0] } as MarkerConfig,
      this.markerConfig,
    );
  }

  get normalizedDetailViewConfig(): DetailViewConfig {
    return utilService.assignWithoutUndefined(
      { resolution: 10000, longitudeDegree: 0.002 } as DetailViewConfig,
      this.detailViewConfig,
    );
  }

  get effectiveExtendedMarkerClusterGroupOptions(): ExtendedMarkerClusterGroupOptions {
    const options = utilService.assignWithoutUndefined(
      {
        threshold: { low: 10, high: 100 },
        groupOptions: {},
      } as ExtendedMarkerClusterGroupOptions,
      typeof this.withMarkerCluster === 'boolean' ? undefined : this.withMarkerCluster,
    );
    return options;
  }

  get effectiveMarkerClusterGroupOptions(): L.MarkerClusterGroupOptions {
    if (!this.withMarkerCluster) { return {}; }

    const defaultOptions: L.MarkerClusterGroupOptions = {
      zoomToBoundsOnClick: false,
      disableClusteringAtZoom: this.mapZoomConfig.maxZoom + 1,
      maxClusterRadius: 200,
      iconCreateFunction: (cluster) => {
        const markers = cluster.getAllChildMarkers();
        const locationIds = markers.map<string>(m => ((m as any).locationId));
        const count = markers.length;
        const threshold = this.effectiveExtendedMarkerClusterGroupOptions.threshold!;
        const level = count <= threshold.low ? 'low' : (count > threshold.high ? 'high' : 'medium');
        const size = level === 'low' ? 40 : (level === 'medium' ? 50 : 65);
        const color = this.effectiveExtendedMarkerClusterGroupOptions.clusterColors ?
          this.effectiveExtendedMarkerClusterGroupOptions.clusterColors(locationIds) : null;
        const styles = (function () {
          if (!color) { return ''; }
          let str = '';
          let mainColorObj: Color | null = null;
          if (color.backgroundColor) {
            mainColorObj = Color(color.backgroundColor);
            str += `background-color: ${mainColorObj.fade(0.1).string()};`; // tslint:disable-line
            str += `border-color: ${mainColorObj.lighten(.33).fade(0.3).string()};`; // tslint:disable-line
          }
          if (color.fontColor) {
            const colorObj = Color(color.fontColor);
            str += `color: ${colorObj.string()};`;
          } else if (mainColorObj) {
            /** determine the font color based on the main color by contrast */
            str += `color: ${mainColorObj.isLight() ? 'black' : 'white'};`;
          }
          return str;
        })();

        return L.divIcon({
          html: `<div class="${this.cssPrefix}__marker-cluster" style="width: ${size}px; height: ${size}px">` +
                  `<div class="${this.cssPrefix}__marker-cluster-inner ${this.cssPrefix}__marker-cluster-inner--${level} ` +
                      'sw-flexbox sw-flexbox--center-items sw-flexbox--center-justify" ' +
                    `style="${styles}"` +
                  '>' +
                    `<span class="sw-flexbox__flex-none">${count}</span>` +
                  '</div>' +
                '</div>',
          className: `${this.cssPrefix}__icon-cont`,
          iconSize: [size, size],
        });
      },
    };
    return Object.assign(
      defaultOptions,
      this.effectiveExtendedMarkerClusterGroupOptions.groupOptions,
    );
  }

  get viewingLayers(): MapControlledLayer[] {
    if (!this.viewDetailLocationId) { return []; }
    const locationDesc = this.locationDescMap[this.viewDetailLocationId];
    if (!locationDesc) { return []; }
    if (!locationDesc.location.detailView) { return []; }

    return locationDesc.location.detailView.layers.map<MapControlledLayer>((l) => {
      return {
        id: l.id,
        name: l.name,
        actions: l.actions || [],
      };
    });
  }

  get viewingDetailViewLayerDesc(): DetailViewLayerDescriptor | null {
    if (!this.viewDetailLocationId || !this.viewDetailLayerId) { return null; }
    if (!this.mapObj) { return null; }
    const locationDesc = this.locationDescMap[this.viewDetailLocationId];
    if (!locationDesc) { return null; }
    const layerDesc = locationDesc.layerMap[this.viewDetailLayerId];
    if (!layerDesc) { return null; }
    return layerDesc;
  }

  get debouncedMapResizeHandler() {
    return _debounce(this.onMapContainerResized, 100);
  }

  get mapBoundsOptions(): boolean | LatLngBounds {
    if (!this.mapBounds) { return false; }
    let northEast = L.latLng(90, 180);
    let southWest = L.latLng(-90, -180);
    if (typeof this.mapBounds !== 'boolean') {
      const mapBound = this.mapBounds as ExtendedMapBoundsOptions;
      northEast = L.latLng(mapBound.northEast.lat, mapBound.northEast.lng);
      southWest = L.latLng(mapBound.southWest.lat, mapBound.southWest.lng);
    }
    return L.latLngBounds(southWest, northEast);
  }

  get minimumZoomValues(): number {
    if (typeof this.minimumZoom === 'boolean') {
      return 3;
    }
    return this.minimumZoom;
  }

  @Watch('locations', { deep: true })
  private onLocationsChange() {
    this.updateLocationDescMap();
  }

  @Watch('viewDetailLocationId')
  private onViewDetailLocationChange(locationId: string | null, oldLocationId: string | null = null) {
    const oldLocation = oldLocationId ? this.locationDescMap[oldLocationId] : null;
    const currLocation = locationId ? this.locationDescMap[locationId] : null;

    if (oldLocation) {
      /** hide the detail view of old location */
      this.detachDetailViewForLocation(oldLocation);
      this.$emit('leave-detail-view', oldLocationId);
    }

    if (!currLocation) {
      this.showRegularMap(true);
      this.viewDetailLayerId = null;
    } else {
      const layerDesc = this.viewDetailLayerId ? currLocation.layerMap[this.viewDetailLayerId] : null;
      if (!layerDesc) { this.viewDetailLayerId = null; }
      /** show the detail view for current location */
      this.showDetailViewForLocation(currLocation);
      this.$emit('enter-detail-view', locationId);
    }
  }

  @Watch('viewDetailLayerId')
  private onViewDetailLayerChange(layerId: string | null, oldLayerId: string | null) {
    if (!this.viewDetailLocationId) { return; }
    const locationDesc = this.locationDescMap[this.viewDetailLocationId];
    if (!locationDesc) { return; }

    const layerDesc = layerId ? locationDesc.layerMap[layerId] : null;
    const oldLayerDesc = oldLayerId ? locationDesc.layerMap[oldLayerId] : null;

    if (oldLayerDesc) {
      this.detachDetailViewLayer(oldLayerDesc);
    }

    if (layerDesc) {
      this.showDetailViewLayer(layerDesc);
    }
  }

  @Watch('mapZoomConfig')
  private onMapZoomConfigChange() {
    /** when zoom config changed, we have to sync the change to leaflet map */
    if (!this.mapObj) { return; }
    this.mapObj.setMaxZoom(this.mapZoomConfig.maxZoom);
  }

  @Watch('withMarkerCluster', { deep: true })
  private onWithMarkerClusterChange() {
    /** XXX: for now property changes in options are not reactive */
    if (!!this.withMarkerCluster && this.mapObj) {
      const locationsVisible = this.isCurrentAnyLocationVisible();

      this.teardownMarkerClusterGroup();

      const locationDescs = Object.values(this.locationDescMap);

      /** remove the marker from mapObj first */
      locationDescs.forEach((ld) => {
        this.addLocationMarkerObj(ld, false);
      });

      this.createMarkerClusterGroup();
      /** add the marker to cluster group then */
      if (locationsVisible) {
        locationDescs.forEach((ld) => {
          if (!this.mapObj) { return; }
          this.addLocationMarkerObj(ld, true);
        });
        this.mapObj.addLayer(this.markerClusterGroup!);
      }
    }
    if (this.withMarkerCluster === false && !!this.markerClusterGroup) {
      if (this.mapObj) {
        const addMarkers = this.mapObj.hasLayer(this.markerClusterGroup);
        this.teardownMarkerClusterGroup();
        if (addMarkers) {
          Object.values(this.locationDescMap)
            .forEach((ld) => {
              if (!this.mapObj) { return; }
              this.addLocationMarkerObj(ld, true);
            });
        }
      }
      this.markerClusterGroup = null;
    }
  }

  private createMarkerClusterGroup() {
    if (this.markerClusterGroup) { return; }
    this.markerClusterGroup = L.markerClusterGroup(this.effectiveMarkerClusterGroupOptions);

    this.markerClusterGroup.on('clusterclick', (event: any) => {
      const cluster: ExtendedMarkerCluster = event.layer;
      cluster.swSuppressPopup = true;
      event.layer.spiderfy();
    });

    this.markerClusterGroup.on('clustermouseover', (event: any) => {
      const cluster: ExtendedMarkerCluster = event.layer;

      if (cluster.swSuppressPopup === true) { return; }
      if (!this.effectiveExtendedMarkerClusterGroupOptions.clusterPopup) { return; }

      if (!cluster.layerDesc) {
        cluster.layerDesc = {
          popupVueInst: null,
          popupData: () => {
            const markers = cluster.getAllChildMarkers();
            const locationIds = markers.map<string>(m => ((m as any).locationId));
            return { locationIds };
          },
        };
      }

      if (!cluster.getPopup()) {
        const count = cluster.getChildCount();
        const threshold = this.effectiveExtendedMarkerClusterGroupOptions.threshold!;
        const level = count <= threshold.low ? 'low' : (count > threshold.high ? 'high' : 'medium');
        const opts = utilService.assignWithoutUndefined(
          {
            offset: [0, level === 'low' ? -8 : (level === 'high' ? -18 : -12)],
          } as ExtendedMarkerClusterGroupOptions['clusterPopup'],
          this.effectiveExtendedMarkerClusterGroupOptions.clusterPopup,
        );
        this.setupPopup(cluster.layerDesc, cluster, opts);
      }

      cluster.openPopup();
    });

    this.markerClusterGroup.on('spiderfied', (event: any) => {
      const cluster: ExtendedMarkerCluster = event.cluster;
      cluster.swSuppressPopup = true;
    });

    this.markerClusterGroup.on('unspiderfied', (event: any) => {
      const cluster: ExtendedMarkerCluster = event.cluster;
      cluster.swSuppressPopup = false;
    });
  }

  private teardownMarkerClusterGroup() {
    if (!!this.markerClusterGroup) {
      this.markerClusterGroup.off();
      this.markerClusterGroup.clearLayers();
      if (this.mapObj) {
        this.mapObj.removeLayer(this.markerClusterGroup);
      }
      this.markerClusterGroup = null;
    }
  }

  private showRegularMap(doShow: boolean) {
    if (this.baseLayer) { this.baseLayer.setOpacity(doShow ? 1 : 0); }
    Object.values(this.locationDescMap)
      .forEach((ld) => {
        if (!this.mapObj) { return; }
        this.addLocationMarkerObj(ld, doShow);
      });
    if (!!this.markerClusterGroup && this.mapObj) {
      if (doShow) {
        this.mapObj.addLayer(this.markerClusterGroup);
      } else {
        this.mapObj.removeLayer(this.markerClusterGroup);
      }
    }
  }

  private showDetailViewForLocation(locationDesc: InternalLocationDescriptor) {
    if (!this.mapObj) { return; }
    if (!locationDesc.location.detailView) { return; }
    if (locationDesc.location.detailView.toLoad === true) { return; }
    if (locationDesc.location.detailView.layers.length <= 0) { return; }

    if (!this.viewDetailLayerId) {
      this.viewDetailLayerId = locationDesc.location.detailView.layers[0].id;
    }

    const viewingLayer = locationDesc.layerMap[this.viewDetailLayerId];
    if (!viewingLayer) { return; }

    this.showRegularMap(false);
  }

  private showDetailViewLayer(layerDesc: DetailViewLayerDescriptor) {
    if (!layerDesc.layerObj || !this.mapObj) { return; }

    layerDesc.layerObj.addTo(this.mapObj);
    Object.values(layerDesc.interestMap)
      .forEach((interestDesc) => {
        interestDesc.markerObj.addTo(this.mapObj!);
      });

    this.$emit('viewing-detail-layer', layerDesc.layer.id);
  }

  private detachDetailViewForLocation(locationDesc: InternalLocationDescriptor) {
    Object.values(locationDesc.layerMap)
      .forEach(this.detachDetailViewLayer);
  }

  private detachDetailViewLayer(layerDesc: DetailViewLayerDescriptor) {
    if (layerDesc.layerObj) {
      layerDesc.layerObj.remove();
    }
    Object.values(layerDesc.interestMap)
      .forEach((interestDesc) => {
        interestDesc.markerObj.remove();
      });
  }

  private setupPopup<D>(desc: MapLayerDescriptor<D>, target: L.Layer, popupConfig: PopupConfig<D> | undefined) {
    function destroyPopupBinding() {
      target.off('popupopen');
      target.off('popupclose');
      target.off('remove');

      const popup = target.getPopup();
      if (popup) {
        popup.setContent('');
      }
      target.closePopup();
      target.unbindPopup();
      destroyVueInst();
    }

    function destroyVueInst() {
      if (desc.popupVueInst) {
        desc.popupVueInst.$destroy();
        desc.popupVueInst = null;
      }
    }

    if (!popupConfig) {
      destroyPopupBinding();
      return;
    }

    if (desc.popupVueInst) {
      (desc.popupVueInst as any).popupConfig = popupConfig;
      (desc.popupVueInst as any).popupData = desc.popupData;
    }

    target.off('popupopen');
    target.on('popupopen', (event) => {
      const popupEvent = event as L.PopupEvent;
      const popup = popupEvent.popup;
      destroyVueInst();
      desc.popupVueInst = new Vue({
        data: () => {
          return {
            popupConfig,
            popupData: desc.popupData,
          };
        },
        i18n: i18nService.i18n,
        render(h: CreateElement): VNode {
          if (typeof this.popupConfig.content === 'string') {
            return (this as any)._v(popupConfig.content);
          }
          if (typeof this.popupConfig.content === 'function') {
            const data = !this.popupData ? undefined : this.popupData();
            return this.popupConfig.content(h, data);
          }
          return (<span>no content</span>) as VNode;
        },
        updated() {
          this.$nextTick(() => {
            popup.update();
          });
        },
      });
      desc.popupVueInst.$mount();
      popup.setContent(desc.popupVueInst.$el as HTMLElement);
    });

    target.off('popupclose');
    target.on('popupclose', (event) => {
      const popupEvent = event as L.PopupEvent;
      const popup = popupEvent.popup;
      popup.setContent('');
      destroyVueInst();
    });

    target.off('remove');
    target.on('remove', (event) => {
      const popup = (event.target as L.Layer).getPopup();
      if (popup) {
        popup.setContent('');
      }
      (event.target as L.Layer).closePopup();
      destroyVueInst();
    });

    if (!target.getPopup()) {
      const opts = utilService.assignWithoutUndefined(
        { className: 'sw-map-popup', maxWidth: 300 } as L.PopupOptions,
        {
          maxWidth: popupConfig.maxWidth,
          maxHeight: popupConfig.maxHeight,
          offset: popupConfig.offset,
        } as L.PopupOptions,
      );
      target.bindPopup('', opts);
    }
  }

  private setPopupData<D>(desc: MapLayerDescriptor<D>, data: () => D) {
    desc.popupData = data;
    if (desc.popupVueInst) {
      (desc.popupVueInst as any).popupData = desc.popupData;
    }
  }

  private setupDraggable(markerConfig: MapMarkerConfig | undefined, markerObj: L.Marker) {
    if (markerConfig && markerObj.dragging) {
      if (markerConfig.draggable === true) {
        markerObj.dragging.enable();
      } else {
        markerObj.dragging.disable();
      }
    }
  }

  private addLocationMarkerObj(ld: InternalLocationDescriptor, doAdd: boolean) {
    if (!ld.markerObj || !this.mapObj) { return; }
    if (!!this.markerClusterGroup && ld.clusterable) {
      if (doAdd) {
        this.markerClusterGroup.addLayer(ld.markerObj);
        this.setupDraggable(ld.location.marker, ld.markerObj);
      } else {
        this.markerClusterGroup.removeLayer(ld.markerObj);
      }
    } else {
      if (doAdd) {
        ld.markerObj.addTo(this.mapObj);
        this.setupDraggable(ld.location.marker, ld.markerObj);
      } else {
        ld.markerObj.remove();
      }
    }
  }

  private updateLocationDescMap() {
    const self = this;

    function destroyLocation(ld: InternalLocationDescriptor) {
      delete self.locationDescMap[ld.location.id];
      self.locationDescMap = Object.assign({}, self.locationDescMap);
      self.addLocationMarkerObj(ld, false);
      /** destroy detailView layers */
      self.detachDetailViewForLocation(ld);
    }

    const locationsVisible = this.isCurrentAnyLocationVisible();

    /** destroy all locations that are not necessary any more */
    const toDestroy: InternalLocationDescriptor[] = _differenceWith(
      Object.values(this.locationDescMap),
      this.locations,
      (a, b) => (a.location.id === b.id),
    );
    toDestroy.forEach((ld) => {
      destroyLocation(ld);
    });

    /** update all remaining locations currently left in descMap */
    this.locations.forEach((l) => {
      const ld = this.locationDescMap[l.id];
      if (!ld || !ld.markerObj) { return; }

      ld.location = l;

      ld.markerObj.setLatLng(l.coordinate);

      ld.markerObj.setIcon(this.genMarkerIcon(l.marker));

      const inputClusterable = l.clusterable !== false;
      if (ld.clusterable !== inputClusterable) {
        this.addLocationMarkerObj(ld, false);
        ld.clusterable = inputClusterable;
        if (locationsVisible) { this.addLocationMarkerObj(ld, true); }
      }

      this.setupPopup(ld, ld.markerObj, l.popup);

      this.setupDraggable(ld.location.marker, ld.markerObj);

      /** update detailView layers */
      this.updateDetailViewLayerDescMapForLocation(ld);
    });

    /** add all new locations to descMap */
    const toAdd: MapLocation[] = _differenceWith(
      this.locations,
      Object.values(this.locationDescMap),
      (a, b) => (a.id === b.location.id),
    );
    toAdd.forEach((l) => {
      const markerObj = L.marker(l.coordinate, { icon: this.genMarkerIcon(l.marker) });
      /** we need markerObj to carry locationId that will be used when only markerObj is available */
      (markerObj as any).locationId = l.id;
      const ld: InternalLocationDescriptor = {
        markerObj,
        location: l,
        clusterable: l.clusterable !== false,
        popupVueInst: null,
        layerMap: {},
      };
      Vue.set(this.locationDescMap, l.id, ld);

      /** add detailView layers */
      this.updateDetailViewLayerDescMapForLocation(ld);

      this.setupPopup(ld, ld.markerObj, l.popup);

      ld.markerObj.on('dragend', () => { this.onLocationDragEnd(ld); });

      if (locationsVisible) {
        this.addLocationMarkerObj(ld, true);
      }
    });
  }

  private updateDetailViewLayerDescMapForLocation(locationDesc: InternalLocationDescriptor) {
    if (!locationDesc.location.detailView || locationDesc.location.detailView.toLoad === true) {
      this.detachDetailViewForLocation(locationDesc);
      locationDesc.layerMap = {};
      return;
    }

    const self = this;

    function setupNewLayer(layerDesc: DetailViewLayerDescriptor, coor: [number, number]) {
      const layer = layerDesc.layer;
      layerDesc.layerObj = L.imageOverlay(
        layer.imgSrc,
        [[0, 0], [self.normalizedDetailViewConfig.longitudeDegree, self.normalizedDetailViewConfig.longitudeDegree]],
      );

      const layerObj = layerDesc.layerObj;
      layerDesc.layerObj.on('load', () => {
        const isNowShowing = self.viewDetailLocationId === locationDesc.location.id && self.viewDetailLayerId === layer.id;

        layerObj.setBounds(self.getBoundsForDetailViewImage(
          layerObj.getElement()!,
          coor,
        ));
        self.updateDetailViewInterestDescMapForLayer(layerDesc, isNowShowing);
      });

      /** now we may want to add it in the map if needed */
      const isShowing = self.viewDetailLocationId === locationDesc.location.id && self.viewDetailLayerId === layer.id;
      if (isShowing && self.mapObj) {
        self.showDetailViewLayer(layerDesc);
      }
    }

    const layers = locationDesc.location.detailView.layers;

    /** destroy unnecessary layers */
    const toDestroy: DetailViewLayerDescriptor[] = _differenceWith(
      Object.values(locationDesc.layerMap),
      layers,
      (a, b) => (a.layer.id === b.id),
    );
    toDestroy.forEach((ld) => {
      delete locationDesc.layerMap[ld.layer.id];
      locationDesc.layerMap = Object.assign({}, locationDesc.layerMap);
      this.detachDetailViewLayer(ld);
    });

    /** update remaining layers */
    layers.forEach((layer) => {
      const layerDesc = locationDesc.layerMap[layer.id];
      if (!layerDesc) { return; }

      layerDesc.layer = layer;

      if (layer.toLoad === true) {
        this.detachDetailViewLayer(layerDesc);
        layerDesc.layerObj = null;
        layerDesc.interestMap = {};
        return;
      }

      if (layerDesc.layerObj) {
        layerDesc.layerObj.setUrl(layer.imgSrc);
      } else {
        setupNewLayer(layerDesc, locationDesc.location.coordinate);
      }
    });

    /** add new layers */
    const toAdd: DetailViewLayer[] = _differenceWith(
      layers,
      Object.values(locationDesc.layerMap),
      (a, b) => (a.id === b.layer.id),
    );
    toAdd.forEach((layer) => {
      const ld: DetailViewLayerDescriptor = {
        layer,
        layerObj: null,
        popupVueInst: null,
        interestMap: {},
      };
      Vue.set(locationDesc.layerMap, layer.id, ld);

      if (layer.toLoad === true) { return; }

      setupNewLayer(ld, locationDesc.location.coordinate);
    });
  }

  private updateDetailViewInterestDescMapForLayer(layerDesc: DetailViewLayerDescriptor, isShowing: boolean) {
    if (!layerDesc.layerObj) { return; }

    const interests = layerDesc.layer.interests;
    const bounds = layerDesc.layerObj.getBounds();

    /** destroy unnecessary interests */
    const toDestroy: DetailViewInterestDescriptor[] = _differenceWith(
      Object.values(layerDesc.interestMap),
      interests,
      (a, b) => (a.interest.id === b.id),
    );
    toDestroy.forEach((interestDesc) => {
      delete layerDesc.interestMap[interestDesc.interest.id];
      layerDesc.interestMap = Object.assign({}, layerDesc.interestMap);
      interestDesc.markerObj.remove();
    });

    /** update remaining interests */
    interests.forEach((interest) => {
      const interestDesc = layerDesc.interestMap[interest.id];
      if (!interestDesc) { return; }

      interestDesc.interest = interest;

      interestDesc.markerObj.setLatLng(this.getLatlngForInterest(interest, bounds));

      interestDesc.markerObj.setIcon(this.genMarkerIcon(interest.marker));

      this.setupPopup(interestDesc, interestDesc.markerObj, interest.popup);

      this.setupDraggable(interestDesc.interest.marker, interestDesc.markerObj);
    });

    /** add new interests */
    const toAdd: DetailViewInterest[] = _differenceWith(
      interests,
      Object.values(layerDesc.interestMap),
      (a, b) => (a.id === b.interest.id),
    );
    toAdd.forEach((interest) => {
      const markerObj = L.marker(
        this.getLatlngForInterest(interest, bounds),
        { icon: this.genMarkerIcon(interest.marker) },
      );
      const interestDesc: DetailViewInterestDescriptor = {
        markerObj,
        interest,
        popupVueInst: null,
      };
      Vue.set(layerDesc.interestMap, interest.id, interestDesc);

      this.setupPopup(interestDesc, interestDesc.markerObj, interest.popup);

      interestDesc.markerObj.on('dragend', () => { this.onInterestDragEnd(interestDesc, layerDesc); });

      if (isShowing && this.mapObj) {
        markerObj.addTo(this.mapObj);
        this.setupDraggable(interestDesc.interest.marker, interestDesc.markerObj);
      }
    });
  }

  private updateDetailViewPerZoomChange() {
    if (this.zoom > this.mapZoomConfig.maxRequestZoom) {
      if (this.viewDetailLocationId === null && !this.isLayerControlVisible) {
        /** determine the location for showing detailView only when entering the detailView first time */
        const l = this.getClosestLocation(true, true);
        this.viewDetailLocationId = (l ? l.location.id : null);
      }
      this.isLayerControlVisible = true;
    } else {
      this.viewDetailLocationId = null;
      this.isLayerControlVisible = false;
    }
  }

  private getLocationsInView(): InternalLocationDescriptor[] {
    if (!this.mapObj) { return []; }
    const viewBounds = this.mapObj.getBounds();
    return Object.values(this.locationDescMap)
      .filter(ld => (!!ld.markerObj && viewBounds.contains(ld.markerObj.getLatLng())));
  }

  private getClosestLocation(inView: boolean = true, hasDetailView: boolean = false): InternalLocationDescriptor | null {
    if (!this.mapObj) { return null; }

    let candidates: InternalLocationDescriptor[] = [];
    if (inView) {
      candidates = this.getLocationsInView();
    } else {
      candidates = Object.values(this.locationDescMap);
    }

    let currDist = Infinity;
    let closestLocation: InternalLocationDescriptor | null = null;
    const center = this.mapObj.getCenter();

    candidates.forEach((c) => {
      if (hasDetailView && !c.location.detailView) { return; }
      const dist = center.distanceTo(c.location.coordinate);
      if (dist < currDist) {
        currDist = dist;
        closestLocation = c;
      }
    });

    return closestLocation;
  }

  // return [leftBottomPoint, rightTopPoint], Bound does not represent the real bound of building.
  private getBoundsForDetailViewImage(image: HTMLImageElement, center: [number, number]): L.LatLngBounds {
    const aspectRatio = image.naturalWidth / image.naturalHeight;  // current accept Landscape image.
    const widthOffset = this.normalizedDetailViewConfig.longitudeDegree / 2; // in LatLng Degree.
    const lbPoint: [number, number] = [center[0] - widthOffset / aspectRatio, center[1] - widthOffset];
    const rtPoint: [number, number] = [center[0] + widthOffset / aspectRatio, center[1] + widthOffset];
    return L.latLngBounds([lbPoint, rtPoint]);
  }

  private getLatlngForInterest(interest: DetailViewInterest, bounds: L.LatLngBounds): L.LatLng {
    const origin = bounds.getSouthWest();
    const lngDiff = bounds.getEast() - bounds.getWest();
    const latDiff = bounds.getNorth() - bounds.getSouth();
    return L.latLng(
      origin.lat + (interest.xy[1] * latDiff / this.normalizedDetailViewConfig.resolution),
      origin.lng + (interest.xy[0] * lngDiff / this.normalizedDetailViewConfig.resolution),
    );
  }

  private getXyFromCoordinateInBounds(latLng: L.LatLng, bounds: L.LatLngBounds): [number, number] {
    const origin = bounds.getSouthWest();
    const lngDiff = bounds.getEast() - bounds.getWest();
    const latDiff = bounds.getNorth() - bounds.getSouth();
    return [
      (latLng.lng - origin.lng) / lngDiff * this.normalizedDetailViewConfig.resolution,
      (latLng.lat - origin.lat) / latDiff * this.normalizedDetailViewConfig.resolution,
    ];
  }

  private genMarkerIcon(config?: Partial<MarkerConfig>): L.DivIcon {
    const markerConfig: MarkerConfig = utilService.assignWithoutUndefined({}, this.normalizedGlobalMarkerConfig, config);
    const divIcon = L.divIcon({
      html: `<span class="sw-icon" style="width:${markerConfig.size}px;height: ${markerConfig.size}px;display:block">` +
        `<i class="sw-icon__inner sw-font-icon icon-${markerConfig.name}" ` +
          `style="color:${markerConfig.color};font-size:${markerConfig.size}px;"` +
        '></i>' +
        '</span>',
      className: `${this.cssPrefix}__icon-cont`,
      iconSize: [markerConfig.size, markerConfig.size],
      iconAnchor: [markerConfig.size / 2 + markerConfig.anchorOffset[0], markerConfig.size + markerConfig.anchorOffset[1]],
      popupAnchor: [0 - markerConfig.anchorOffset[0], -markerConfig.size - markerConfig.anchorOffset[1]],
    });
    return divIcon;
  }

  private isCurrentAnyLocationVisible() {
    const initDescs = Object.values(this.locationDescMap);
    const locationsVisible = initDescs.length <= 0 || !initDescs.every((ld) => {
      const isInLayer = (!!this.markerClusterGroup && ld.clusterable) ? this.markerClusterGroup.hasLayer(ld.markerObj) :
        (!!this.mapObj && this.mapObj.hasLayer(ld.markerObj));
      return !isInLayer;
    });
    return locationsVisible;
  }

  private initMap() {
    const staticMapOpt: L.MapOptions = {
      attributionControl: true,
      zoomControl: false,
      dragging: false,
      doubleClickZoom: false,
      boxZoom: false,
      keyboard: false,
      scrollWheelZoom: false,
    };
    const mapOpt: L.MapOptions = { attributionControl: true, zoomControl: false };
    if (this.mapBoundsOptions) {
      mapOpt.maxBounds = this.mapBoundsOptions as LatLngBounds;
    }
    if (!!this.minimumZoom) {
      mapOpt.minZoom = this.minimumZoomValues as number;
    }
    const map = L.map(this.$refs.mapDiv as HTMLElement, this.enableInteraction ? mapOpt : staticMapOpt)
      .setView(this.initCenter, this.initZoom);
    this.mapObj = map;
    this.baseLayer = L.tileLayer(this.serverOptions.sourceUrl, {
      maxNativeZoom: this.mapZoomConfig.maxRequestZoom,
      maxZoom: this.mapZoomConfig.maxZoom,
      attribution: this.serverOptions.attribution,
    })
      .addTo(map);

    this.mapObj.on('zoomend', this.onZoomEnd);
    domService.resizeObserver.on(this.$refs.mapDiv as HTMLElement, this.debouncedMapResizeHandler);

    this.initPrinter(this.baseLayer);

    if (!!this.withMarkerCluster) {
      this.createMarkerClusterGroup();
      this.mapObj.addLayer(this.markerClusterGroup!);
    }

    this.$nextTick(() => {
      // Fix case when InitMap called, sw-map container still in CSS animation, width/height were incorrect.
      // https://github.com/Leaflet/Leaflet/issues/4835
      this.mapObj!.invalidateSize();
    });
  }

  private initPrinter(tileLayer: L.TileLayer): void {
    if (this.mapObj && tileLayer && this.enableExport) {
      this.printer = (L as any).easyPrint({
        tileLayer,
        hidden: true,
        exportOnly: true,
        // hideClasses: ['sw-map__zoom-control'], // better capsule zoom-control with leaflet Control Class.
        hideControlContainer: true,
      })
      .addTo(this.mapObj);
    }
  }

  /**
   * @public
   * Move to the coordinate of specified locaiton and show its detail view if available
   */
  public showDetailView(locationId: string, layerId?: string): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if (!this.mapObj) { return resolve(false); }
      const locationDesc = this.locationDescMap[locationId];
      if (!locationDesc) { return resolve(false); }
      const location = locationDesc.location;
      const layerDesc = layerId ? locationDesc.layerMap[layerId] :
        (location.detailView && location.detailView.layers.length > 0 ? locationDesc.layerMap[location.detailView.layers[0].id] : null);
      if (!layerDesc) { return resolve(false); }

      const self = this;
      function showIt() {
        function doneMove() {
          self.mapObj!.off('moveend', doneMove);
          self.viewDetailLayerId = layerDesc!.layer.id;
          resolve(true);
        }

        self.mapObj!.on('moveend', doneMove);
        self.mapObj!.setView(location!.coordinate, self.mapZoomConfig.maxRequestZoom + 1);
      }

      /** leave the detail view if it is not for the specified one */
      if (this.viewDetailLocationId && this.viewDetailLocationId !== locationId) {
        this.mapObj.once('zoomend', showIt);
        this.mapObj.setZoom(this.mapZoomConfig.maxRequestZoom);
      } else {
        showIt();
      }
    });
  }

  /**
   * @public
   * Move the center of the map to the specified location
   */
  public locateLocation(locationId: string): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if (!this.mapObj) { return resolve(false); }
      const locationDesc = this.locationDescMap[locationId];
      if (!locationDesc) { return resolve(false); }

      const currZoom = this.mapObj.getZoom();
      const zoomTo = Math.min(this.mapZoomConfig.maxRequestZoom, currZoom);
      const self = this;

      function doneMove() {
        self.mapObj!.off('moveend', doneMove);
        resolve(true);
      }

      self.mapObj!.on('moveend', doneMove);
      self.mapObj!.setView(locationDesc.location.coordinate, zoomTo);
    });
  }

  /**
   * @public
   */
  public exportMap(): void {
    if (this.printer) {
      this.printer.printMap('CurrentSize', 'exportMap');
    } else {
      // console.warn('Did not init Printer');
    }
  }

  /**
   * @public
   */
  public getDetailViewInfo(): { locationId: string | null, layerId: string | null } {
    return {
      locationId: this.viewDetailLocationId,
      layerId: this.viewDetailLayerId,
    };
  }

  /**
   * @public
   * Convert a point relative to container top-left to the coordinate in the viewing detail layer,
   * which means it only works when viewing one detail view, otherwise `null` is returned.
   */
  public containerPointToDetailViewCoordinate(top: number, left: number): [number, number] | null {
    if (!this.mapObj) { return null; }
    if (!this.viewingDetailViewLayerDesc || !this.viewingDetailViewLayerDesc.layerObj) { return null; }
    const bounds = this.viewingDetailViewLayerDesc.layerObj.getBounds();
    const coor = this.mapObj.containerPointToLatLng([left, top]);
    return this.getXyFromCoordinateInBounds(coor, bounds);
  }

  private resetMap(): void {
    if (this.mapObj) {
      this.mapObj.setView(this.initCenter, this.initZoom);
    }
    this.$emit('reset-map');
  }

  private onMapContainerResized() {
    if (!this.mapObj) { return; }
    this.mapObj.invalidateSize();
  }

  private onLocationDragEnd(locationDesc: InternalLocationDescriptor): void {
    const coor = locationDesc.markerObj.getLatLng();
    this.$emit('location-relocated', {
      locationId: locationDesc.location.id,
      location: locationDesc.location,
      coordinate: [coor.lat, coor.lng],
    } as MapLocationRelocatedEventData);
  }

  private onInterestDragEnd(interestDesc: DetailViewInterestDescriptor, layerDesc: DetailViewLayerDescriptor): void {
    if (!layerDesc.layerObj) { return; }
    const bounds = layerDesc.layerObj.getBounds();
    const coor = interestDesc.markerObj.getLatLng();
    this.$emit('interest-relocated', {
      interestId: interestDesc.interest.id,
      interest: interestDesc.interest,
      xy: this.getXyFromCoordinateInBounds(coor, bounds),
    } as InterestRelocatedEventData);
  }

  private onZoomEnd(): void {
    if (this.mapObj) {
      const currZoom = this.mapObj.getZoom();
      if (this.zoomChangeFromControl && currZoom !== this.zoom) {
        this.mapObj.setZoom(this.zoom);
      } else {
        /** the zooming really ended */
        this.zoom = currZoom;
        this.zoomChangeFromControl = false;
        this.updateDetailViewPerZoomChange();
        this.$emit('zoom-change', currZoom);
      }
    }
  }

  private handleControlZoomChange(value: number): void {
    if (this.mapObj && value !== this.zoom) {
      this.zoomChangeFromControl = true;
      this.zoom = value;
      this.mapObj.setZoom(value);
    }
  }

  @Provide()
  private claimLayerAction(layerId: string, actionKey: string) {
    this.$emit('layer-action', { layerId, actionKey } as DetailViewLayerActionEventData);
  }

  private mounted() {
    this.initMap();
    this.updateLocationDescMap();
  }

  private beforeDestroy() {
    /** release all resources */
    if (this.mapObj) {
      if (this.markerClusterGroup) {
        this.markerClusterGroup.clearLayers();
        this.markerClusterGroup = null;
      }
      if (this.printer) {
        this.printer.remove();
        this.printer = null;
      }
      if (this.mapObj) {
        this.mapObj.remove();
        this.mapObj = null;
      }
      this.baseLayer = null;
      this.locationDescMap = {};
    }

    domService.resizeObserver.off(this.$refs.mapDiv as HTMLElement, this.debouncedMapResizeHandler);
  }
}
